<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useInventoryDashboardStore } from '../pages/useInventoryDashboardStore';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import { InventoryDashboardViewModel } from '../pages/inventoryDashboardViewModel';
import { i18n } from '@/i18n';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';
import { MetricTypes } from '../domains/entities/Dashboard';
import { computed } from 'vue';
import { DashboardService } from '../domains/services/DashboardService';
import { HeaderList, isTableType } from '@/coreDomainsModule/table';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
const t = i18n.t;

const store = useInventoryDashboardStore();
const { state } = storeToRefs(store);
const {} = store;
const vm = new InventoryDashboardViewModel();

const isAggregateBusiness = computed(() => DashboardService.isMetricTypeByAggBiz(state.value.information.metricType));

const baseHeaderKeys = [
  { key: 'checkbox', width: 52 },
  { key: 'detail', width: 56 },
  { key: 'warehouseNumber', width: 160 },
  { key: 'warehouse', width: 160 },
  { key: 'supplierNumber', width: 160 },
  { key: 'supplierName', width: 160 },
  { key: 'businessNumber', width: 160 },
  { key: 'businessName', width: 160 },
  { key: 'package', width: 74 },
];

const aggregateBusinessBaseHeaderKeys = [
  { key: 'manufacturer', width: 160 },
  { key: 'aggregateBusinessResponsibleUsers', width: 160 },
];

const baseBusinessBaseHeaderKeys = [
  { key: 'customerNumber', width: 160 },
  { key: 'customerName', width: 160 },
  { key: 'salesDestinationNumber', width: 160 },
  { key: 'salesDestinationName', width: 160 },
  { key: 'endUser', width: 160 },
  { key: 'baseBusinessResponsibleUsers', width: 160 },
];

const customHeaderKeys = {
  [MetricTypes.Arrival]: [{ key: 'arrival', width: 128 }],
  [MetricTypes.CustomerForecast]: [{ key: 'customerForecast', width: 128 }],
  [MetricTypes.ForecastAccuracy]: [
    { key: 'salesResult', width: 128 },
    { key: 'customerForecast', width: 128 },
    { key: 'forecastAccuracy', width: 128 },
  ],
  [MetricTypes.Inventory]: [{ key: 'endInventory', width: 128 }],
  [MetricTypes.OrderAmount]: [{ key: 'orderAmount', width: 128 }],
  [MetricTypes.SalesResult]: [{ key: 'salesResult', width: 128 }],
  [MetricTypes.SupplierForecast]: [{ key: 'supplierForecast', width: 128 }],
  [MetricTypes.Turnover]: [
    { key: 'turnover', width: 128 },
    { key: 'salesResult', width: 128 },
    { key: 'customerForecast', width: 128 },
    { key: 'endInventory', width: 128 },
  ],
};

const fullHeaderOptions = computed(() => [
  ...baseHeaderKeys,
  ...(isAggregateBusiness.value ? aggregateBusinessBaseHeaderKeys : baseBusinessBaseHeaderKeys),
  ...customHeaderKeys[state.value.information.metricType],
  { key: 'headerModify', width: 57 },
]);
const fullHeaderKeys = computed(() => fullHeaderOptions.value.map((h) => h.key));
const tableType = computed(() => {
  const rawTableType =
    'inventoryDashboardByDimension' +
    state.value.information.metricType
      .toLowerCase()
      .split('_')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join('');

  if (!isTableType(rawTableType)) {
    console.error(`Invalid table type: ${rawTableType}`);
    throw new Error(`Invalid table type: ${rawTableType}`);
  }
  return rawTableType;
});

const { headerList, updateDisplayHeader } = useTableHeader(fullHeaderKeys, tableType, []);
const modifiableHeaders = computed(
  (): HeaderList =>
    fullHeaderOptions.value
      .filter((h) => !['checkbox', 'detail', 'headerModify'].includes(h.key))
      .map((h) => ({
        key: h.key,
        text: vm.convertHeaderKeyToDisplayValue(h.key),
        isShown: headerList.value.includes(h.key),
      }))
);

const gridTemplateColumns = computed(() =>
  fullHeaderOptions.value.filter((header) => headerList.value.includes(header.key)).map((h) => `minmax(${h.width}px, ${h.width}fr)`)
);

/**
 * チェックされたかどうか
 * @param businessId - 対象の取引ID
 * @param isChecked - チェック済みかどうか
 */
const onCheckedRow = (businessId: string, isChecked: boolean) => {
  if (isChecked && !state.value.checkedBusinessId.includes(businessId)) {
    state.value.checkedBusinessId.push(businessId);
  } else if (!isChecked && state.value.checkedBusinessId.includes(businessId)) {
    state.value.checkedBusinessId = state.value.checkedBusinessId.filter((b) => b !== businessId);
  }
};

/**
 * ヘッダーを切り替える処理
 * @param headerKey - ヘッダーキー
 * @param isHide - 隠すかどうか
 */
const onToggleHeader = async (headerKey: string, isHide: boolean) => {
  await updateDisplayHeader(headerKey, isHide);
};
</script>
<template>
  <div class="list-view">
    <div v-for="(dimensionValueGroup, index) in state.information.dimensionValueGroups" :key="index" class="item">
      <div class="item__title">
        <div class="item__title--dimension">
          {{ t(vm.dimensionTypesMapper[state.information.dimensionType] ?? '') }}
        </div>
        <div class="item__title--name">
          <template
            v-if="
              dimensionValueGroup.dimensionValues.name ||
              dimensionValueGroup.dimensionValues.value ||
              dimensionValueGroup.dimensionValues.unit
            "
          >
            {{ dimensionValueGroup.dimensionValues.name }} {{ dimensionValueGroup.dimensionValues.value }}
            {{ dimensionValueGroup.dimensionValues.unit }}
          </template>
          <template v-else>
            {{ t(UNASSIGNED_COLUMN) }}
          </template>
        </div>
      </div>
      <div class="item__metric-type">
        {{ t(vm.metricTypesMapper[state.information.metricType] ?? '') }}
      </div>
      <div class="item__table base-table-wrapper">
        <EzTable :grid-template-columns="gridTemplateColumns" sticky-header is-vertical-scrollable>
          <template #header>
            <template v-for="header in headerList.slice(0, -1)" :key="header">
              <EzHeader v-if="header === 'checkbox'" class="item__table__header"><RowCheckBox /></EzHeader>
              <EzHeader v-else-if="header === 'detail'" :content-padding="0" force-center class="item__table__header" />
              <EzHeader v-else class="item__table__header">
                <div class="item__table__header__inner">
                  {{ vm.convertHeaderKeyToDisplayValue(header) }}
                  <!-- <ArrowIcon
                    :asc="convertHeaderKeyToSortKey(header) === sortTarget ? sortType === 'ASC' : false"
                    @on-click="() => onClickSortArrowHandler(header)"
                  />-->
                </div>
              </EzHeader>
            </template>
            <EzHeader sticky="right">
              <TableHeaderModifyButton :modifiable-header-list="modifiableHeaders" @toggle="onToggleHeader" />
            </EzHeader>
          </template>
          <template #body>
            <EzTableRow v-for="(data, index1) in dimensionValueGroup.businesses" :key="index1" :grid-template-columns="gridTemplateColumns">
              <template v-for="header in headerList.slice(0, -1)" :key="header">
                <EzCell
                  v-if="header === 'checkbox'"
                  :is-highlighted="state.checkedBusinessId.includes(data.businessId)"
                  class="item__table__cell"
                >
                  <RowCheckBox @change-status="(isChecked) => onCheckedRow(data.businessId, isChecked)" />
                </EzCell>
                <EzCell
                  v-else-if="header === 'detail'"
                  :content-padding="0"
                  :is-highlighted="state.checkedBusinessId.includes(data.businessId)"
                  class="item__table__cell"
                >
                  <RouterLink
                    v-if="data.businessId"
                    :to="
                      isAggregateBusiness
                        ? { name: 'recommend-order', params: { id: data.businessId } }
                        : { name: 'customer-forecast-timeline', params: { defaultBusinessId: data.businessId } }
                    "
                    target="_blank"
                  >
                    <TextButton class="table-detail-button" button-style="btn-secondary" :on-click="() => {}">
                      {{ t('詳細') }}
                    </TextButton>
                  </RouterLink>
                </EzCell>
                <EzCell
                  v-else-if="
                    ['aggregateBusinessResponsibleUsers', 'baseBusinessResponsibleUsers', 'forecastForReceiptResponsibleUsers'].includes(
                      header
                    )
                  "
                  :is-highlighted="state.checkedBusinessId.includes(data.businessId)"
                  class="item__table__cell"
                >
                  {{ vm.convertUserAccountToDisplayValue(data[header]) }}
                </EzCell>
                <EzCell
                  v-else-if="vm.numberHeaderKeys.includes(header)"
                  is-number
                  :is-highlighted="state.checkedBusinessId.includes(data.businessId)"
                  class="item__table__cell"
                >
                  {{ vm.convertNumberStringToDisplayValue(header, data[header]) }}
                </EzCell>
                <EzCell v-else :is-highlighted="state.checkedBusinessId.includes(data.businessId)" class="item__table__cell">
                  {{ data[header] ?? '--' }}</EzCell
                >
              </template>
              <EzCell />
            </EzTableRow>
          </template>
        </EzTable>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.list-view {
  display: grid;
  gap: 16px;
}
.item {
  display: grid;
  gap: 8px;
  padding: 24px 16px 16px 16px;
  border-radius: 8px;
  border: 1px solid $borderDefault;
  box-shadow: 0px 0px 22px 0px #80808066;

  &__title {
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 22px;
    * {
      color: $textSecondary;
      font-size: 22px;
      font-weight: 700;
    }
  }

  &__metric-type {
    padding: 4px 0;
    color: $textSecondary;
    font-size: 16px;
    font-weight: 700;
  }

  &__table {
    overflow-x: auto;
    :deep(.table__header-container) {
      width: 100%;
    }
    &__header {
      width: 100%;
      height: 36px;
      font-weight: 700;
      font-size: 14px;
      line-height: 1;
      &__inner {
        width: 100%;
        display: grid;
        grid-template-columns: max-content max-content;
        justify-content: space-between;
        align-items: center;
      }
    }
    &__cell {
      height: 24px;
      font-weight: 400;
      font-size: 12px;
      line-height: 1;
    }
  }
}

.table-detail-button {
  display: grid;
  justify-content: center;
  align-items: center;
  font-family: Noto Sans JP;
  padding: unset;
  height: 16px;
  width: 56px;
  background-color: $semanticWhite;
  color: $semanticPrimary;
  border: 1px solid $semanticPrimary;
  font-size: 12px;
  line-height: 12px;
  font-weight: 700;
}
</style>
